import { type AxiosError, isAxiosError } from "axios";

import { type ErrorType } from "@vega-api/configs/customClientQuery";

import { type ErrorResponse } from "../types";

export type { ErrorResponse };

/**
 * Configuration options for error message handling
 * @property {string} [defaultMessage] - Fallback message when no error message is found
 * @property {string} [defaultTitle] - Fallback title when no error title is found
 */
export type ErrorMessageOptions = {
  defaultMessage?: string;
  defaultTitle?: string;
};

/**
 * Utility function to extract and format error messages from API responses.
 * This function handles different types of errors and provides a consistent way to display them.
 *
 * Error message priority:
 * 1. API response detail field (from response.data.detail)
 * 2. API response error message (from response.data.errors.message)
 * 3. HTTP status text (from response.statusText)
 * 4. Custom default message (from options.defaultMessage)
 * 5. Error object's message property
 * 6. Generic "An unexpected error occurred" message
 *
 * Error title priority:
 * 1. API response title field (from response.data.title)
 * 2. Custom default title (from options.defaultTitle)
 * 3. Generic "Error"
 *
 * @param error - The error object from API calls or other sources
 * @param options - Optional configuration for error message handling
 * @returns Object containing formatted title, description, and optional statusCode for error display
 *
 * @example
 * // Basic usage
 * const { title, description } = getErrorMessage(error);
 *
 * @example
 * // With custom defaults
 * const { title, description } = getErrorMessage(error, {
 *   defaultTitle: "Operation Failed",
 *   defaultMessage: "Something went wrong"
 * });
 *
 * @example
 * // In a React component with toast
 * const showToast = useToast();
 *
 * useSomeApiCall({
 *   mutation: {
 *     onError: (error) => {
 *       const { title, description } = getErrorMessage(error, {
 *         defaultTitle: m.errors_modalTitle()
 *       });
 *       showToast({
 *         title,
 *         description,
 *         type: "error"
 *       });
 *     }
 *   }
 * });
 *
 * @example
 * // With status code handling
 * const { statusCode, title, description } = getErrorMessage(error);
 * if (statusCode === 409) {
 *   // Handle conflict error specifically
 * }
 */
export const getErrorMessage = (
  error: ErrorType<unknown>,
  options: ErrorMessageOptions = {},
): { title: string; description: string; statusCode?: number } => {
  let errorMessage: string | undefined;
  let errorTitle: string | undefined;
  let statusCode: number | undefined;

  if (isAxiosError(error)) {
    const apiError = error as AxiosError<ErrorResponse>;

    statusCode = apiError.response?.status;

    errorMessage = apiError.response?.data.detail;
    errorTitle = apiError.response?.data.title;

    if (errorMessage == null || errorMessage.trim() === "") {
      errorMessage = apiError.response?.data.errors?.message;
    }

    if (errorMessage == null || errorMessage.trim() === "") {
      errorMessage = apiError.response?.statusText;
    }
  }

  if (
    (errorMessage == null || errorMessage.trim() === "") &&
    error instanceof Error
  ) {
    errorMessage = error.message;
  }

  const getValidString = (value: string | undefined): string | null =>
    value != null && value.trim() !== "" ? value.trim() : null;

  const title =
    getValidString(errorTitle) ??
    getValidString(options.defaultTitle) ??
    "Error";
  const description =
    getValidString(errorMessage) ??
    getValidString(options.defaultMessage) ??
    "An unexpected error occurred";

  return {
    title,
    description,
    statusCode,
  };
};

type ApiResponseBodyWithErrors = {
  errors?: { message?: string; statusCode?: number; statusText?: string };
  detail?: string;
  title?: string;
};

export const getErrorMessageFromResponseBody = (
  responseBody: ApiResponseBodyWithErrors | null | undefined,
  options: ErrorMessageOptions = {},
): { title: string; description: string; statusCode?: number } => {
  if (responseBody == null || typeof responseBody !== "object") {
    return getErrorMessage(
      new Error("Invalid response") as ErrorType<unknown>,
      options,
    );
  }
  const syntheticError = {
    isAxiosError: true,
    response: {
      data: responseBody,
      status: responseBody.errors?.statusCode,
      statusText: responseBody.errors?.statusText,
    },
  } as ErrorType<ErrorResponse>;

  return getErrorMessage(syntheticError, options);
};
